/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de noviembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import api from '../axiosConfig';
import backgroundGPSService from './backgroundGPSService';

class GPSTrackingService {
  constructor() {
    this.watchId = null;
    this.intervalId = null;
    this.isTracking = false;
    this.trasladoActual = null;
    this.puntosEnEspera = [];
    this.backgroundInitialized = false;

    // Configuración
    this.config = {
      tiempoEnvioSegundos: 15, // Enviar cada 15 segundos (antes 30)
      distanciaMinimaMetros: 10, // Solo enviar si se movió al menos 10 metros (antes 50)
      maxPuntosEnEspera: 5, // Enviar batch cuando acumule 5 puntos (antes 10)
      habilitarBatch: true, // Habilitar envío por lotes
    };

    this.ultimaPosicion = null;

    // Inicializar servicio de background
    this.initializeBackgroundService();
  }

  /**
   * Inicializar servicio de GPS en background
   */
  async initializeBackgroundService() {
    try {
      await backgroundGPSService.inicializar();
      this.backgroundInitialized = true;
      console.log('✓ Servicio de GPS en background inicializado');
    } catch (error) {
      console.error('Error al inicializar servicio background:', error);
    }
  }

  /**
   * Iniciar seguimiento GPS para un traslado
   */
  iniciarSeguimiento(trasladoId, opciones = {}) {
    if (this.isTracking) {
      console.warn('Ya existe un seguimiento GPS activo');
      return;
    }

    this.trasladoActual = trasladoId;
    this.isTracking = true;
    this.config = { ...this.config, ...opciones };

    console.log('=== INICIANDO SEGUIMIENTO GPS ===');
    console.log('Traslado ID:', trasladoId);
    console.log('Configuración:', this.config);

    // Solicitar posición inicial
    this.registrarPuntoInicio();

    // Iniciar watchPosition para seguimiento continuo
    if (navigator.geolocation) {
      this.watchId = navigator.geolocation.watchPosition(
        (position) => this.handlePosicion(position),
        (error) => this.handleError(error),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 5000,
        }
      );

      // Si el batch está habilitado, enviar puntos acumulados periódicamente
      if (this.config.habilitarBatch) {
        this.intervalId = setInterval(() => {
          this.enviarPuntosEnEspera();
        }, this.config.tiempoEnvioSegundos * 1000);
      }
    } else {
      console.error('Geolocalización no soportada en este navegador');
    }
  }

  /**
   * Detener seguimiento GPS
   */
  detenerSeguimiento() {
    if (!this.isTracking) {
      return;
    }

    console.log('=== DETENIENDO SEGUIMIENTO GPS ===');

    // Registrar punto final antes de detener. El id se toma ahora: el envío es asíncrono y más abajo
    // se limpia trasladoActual (antes la petición salía con traslado_id null y fallaba)
    if (this.ultimaPosicion) {
      this.registrarPuntoFin(this.trasladoActual);
    }

    // Enviar puntos pendientes
    if (this.puntosEnEspera.length > 0) {
      this.enviarPuntosEnEspera();
    }

    // Limpiar watchers e intervalos
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isTracking = false;
    this.trasladoActual = null;
    this.ultimaPosicion = null;
    this.puntosEnEspera = [];
  }

  /**
   * Manejar nueva posición recibida
   */
  handlePosicion(position) {
    if (!this.isTracking || !this.trasladoActual) {
      return;
    }

    const coordenadas = {
      latitud: position.coords.latitude,
      longitud: position.coords.longitude,
      precision: position.coords.accuracy,
      velocidad: position.coords.speed ? position.coords.speed * 3.6 : null, // Convertir m/s a km/h
      altitud: position.coords.altitude,
      timestamp: new Date(position.timestamp).toISOString(),
    };

    // Verificar si se movió lo suficiente para registrar
    if (this.ultimaPosicion) {
      const distancia = this.calcularDistancia(
        this.ultimaPosicion.latitud,
        this.ultimaPosicion.longitud,
        coordenadas.latitud,
        coordenadas.longitud
      );

      if (distancia < this.config.distanciaMinimaMetros / 1000) {
        console.log('Movimiento insuficiente, no se registra punto');
        return;
      }
    }

    this.ultimaPosicion = coordenadas;

    if (this.config.habilitarBatch) {
      // Agregar a la cola de puntos en espera
      this.puntosEnEspera.push(coordenadas);

      // Si alcanzó el máximo, enviar inmediatamente
      if (this.puntosEnEspera.length >= this.config.maxPuntosEnEspera) {
        this.enviarPuntosEnEspera();
      }
    } else {
      // Enviar inmediatamente
      this.enviarPunto(coordenadas, 'trayecto');
    }
  }

  /**
   * Manejar error de geolocalización
   */
  handleError(error) {
    console.error('Error de geolocalización:', error);
    switch (error.code) {
      case error.PERMISSION_DENIED:
        console.error('Usuario negó el permiso de geolocalización');
        break;
      case error.POSITION_UNAVAILABLE:
        console.error('Información de ubicación no disponible');
        break;
      case error.TIMEOUT:
        console.error('Timeout al obtener la ubicación');
        break;
      default:
        console.error('Error desconocido de geolocalización');
    }
  }

  /**
   * Registrar punto de inicio del traslado
   */
  async registrarPuntoInicio() {
    if (!navigator.geolocation) {
      console.error('Geolocalización no disponible');
      return;
    }

    try {
      const position = await this.obtenerPosicionActual();
      const coordenadas = {
        latitud: position.coords.latitude,
        longitud: position.coords.longitude,
        precision: position.coords.accuracy,
        velocidad: null,
        altitud: position.coords.altitude,
      };

      this.ultimaPosicion = coordenadas;
      await this.enviarPunto(coordenadas, 'inicio');
      console.log('Punto de inicio registrado:', coordenadas);
    } catch (error) {
      console.error('Error al registrar punto de inicio:', error);
    }
  }

  /**
   * Registrar punto final del traslado
   */
  async registrarPuntoFin(trasladoId = this.trasladoActual) {
    if (!this.ultimaPosicion || !trasladoId) {
      return;
    }

    try {
      const position = await this.obtenerPosicionActual();
      const coordenadas = {
        latitud: position.coords.latitude,
        longitud: position.coords.longitude,
        precision: position.coords.accuracy,
        velocidad: null,
        altitud: position.coords.altitude,
      };

      await this.enviarPunto(coordenadas, 'fin', trasladoId);
      console.log('Punto final registrado:', coordenadas);
    } catch (error) {
      console.error('Error al registrar punto final:', error);
    }
  }

  /**
   * Enviar un punto individual al servidor
   */
  async enviarPunto(coordenadas, tipoPunto = 'trayecto', trasladoId = this.trasladoActual) {
    try {
      const response = await api.post('/gps/registrar-punto', {
        traslado_id: trasladoId,
        latitud: coordenadas.latitud,
        longitud: coordenadas.longitud,
        tipo_punto: tipoPunto,
        precision: coordenadas.precision,
        velocidad: coordenadas.velocidad,
        altitud: coordenadas.altitud,
      });

      console.log('Punto GPS enviado:', tipoPunto, response.data);
      return response.data;
    } catch (error) {
      console.error('Error al enviar punto GPS:', error);
      throw error;
    }
  }

  /**
   * Enviar puntos acumulados en batch
   */
  async enviarPuntosEnEspera() {
    if (this.puntosEnEspera.length === 0) {
      return;
    }

    console.log(`Enviando ${this.puntosEnEspera.length} puntos GPS en batch...`);

    try {
      const response = await api.post('/gps/registrar-puntos-batch', {
        traslado_id: this.trasladoActual,
        puntos: this.puntosEnEspera,
      });

      console.log('Batch GPS enviado:', response.data);
      this.puntosEnEspera = []; // Limpiar cola
      return response.data;
    } catch (error) {
      console.error('Error al enviar batch GPS:', error);

      // Si falla el envío y está en background, guardar localmente
      if (backgroundGPSService.isInBackground() && this.backgroundInitialized) {
        console.log('Guardando puntos localmente para sincronización posterior...');
        for (const punto of this.puntosEnEspera) {
          await backgroundGPSService.guardarPuntoLocal(this.trasladoActual, punto);
        }
        this.puntosEnEspera = []; // Limpiar cola después de guardar localmente
      }
      // No limpiar la cola si no está en background, se reintentará
    }
  }

  /**
   * Obtener posición actual (promesa)
   */
  obtenerPosicionActual() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalización no soportada'));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    });
  }

  /**
   * Calcular distancia entre dos puntos (fórmula Haversine)
   */
  calcularDistancia(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radio de la Tierra en km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distancia en km
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  /**
   * Obtener ruta de un traslado
   */
  async obtenerRuta(trasladoId) {
    try {
      const response = await api.get(`/gps/ruta/${trasladoId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error al obtener ruta GPS:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de un traslado
   */
  async obtenerEstadisticas(trasladoId) {
    try {
      const response = await api.get(`/gps/estadisticas/${trasladoId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error al obtener estadísticas GPS:', error);
      throw error;
    }
  }

  /**
   * Verificar si está rastreando actualmente
   */
  estaRastreando() {
    return this.isTracking;
  }

  /**
   * Obtener ID del traslado actual
   */
  obtenerTrasladoActual() {
    return this.trasladoActual;
  }
}

// Exportar instancia singleton
export default new GPSTrackingService();
